import { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Button,
    Paper,
    Breadcrumbs,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Description as DescriptionIcon,
    NavigateNext as NavigateNextIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import AddFirmwareVersionModal from './AddFirmwareVersionModal';
import { hasPermission } from '../../../utils/permissions';
import api from '../../../utils/api';
import {
    fetchFirmwareVersions,
    fetchFirmwareVersionById,
    addFirmwareVersion,
    updateFirmwareVersion,
    selectFirmwareVersions,
    selectFirmwareVersionsLoading,
    selectFirmwareVersionsError,
    selectFirmwareVersionsSuccessMessage,
    selectFirmwareVersionsTotal,
    selectFirmwareVersionsPage,
    selectFirmwareVersionsPageSize,
    selectFirmwareVersionsSortColumn,
    selectFirmwareVersionsSortDirection,
    selectFirmwareVersionsFilterVersion,
    selectFirmwareVersionsFilterDeviceType,
    selectSelectedFirmwareVersion,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    setFilterVersion,
    setFilterDeviceType,
    deleteFirmwareVersion,
    bulkDeleteFirmwareVersions,
    clearError,
    clearSuccessMessage,
    clearSelectedFirmwareVersion,
} from './slice';
import './FirmwareVersions.css';

function FirmwareVersions() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const firmwareVersions = useSelector(selectFirmwareVersions);
    const loading = useSelector(selectFirmwareVersionsLoading);
    const error = useSelector(selectFirmwareVersionsError);
    const successMessage = useSelector(selectFirmwareVersionsSuccessMessage);
    const total = useSelector(selectFirmwareVersionsTotal);
    const page = useSelector(selectFirmwareVersionsPage);
    const pageSize = useSelector(selectFirmwareVersionsPageSize);
    const sortColumn = useSelector(selectFirmwareVersionsSortColumn);
    const sortDirection = useSelector(selectFirmwareVersionsSortDirection);
    const filterVersion = useSelector(selectFirmwareVersionsFilterVersion);
    const filterDeviceType = useSelector(selectFirmwareVersionsFilterDeviceType);
    const selectedFirmwareVersion = useSelector(selectSelectedFirmwareVersion);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [firmwareVersionToDelete, setFirmwareVersionToDelete] = useState(null);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [deviceTypes, setDeviceTypes] = useState([]);

    // Check permissions
    const canAddFirmwareVersion = hasPermission('firmwareversion', 'addFirmwareVersion');
    const canEditFirmwareVersion = hasPermission('firmwareversion', 'editFirmwareVersion');
    const canDeleteFirmwareVersion = hasPermission('firmwareversion', 'deleteFirmwareVersion');

    // Fetch device types from init endpoint on mount
    useEffect(() => {
        const fetchInitData = async () => {
            try {
                const response = await api.get('/firmwareversions/init');
                if (response && response.deviceTypes) {
                    setDeviceTypes(response.deviceTypes);
                }
            } catch (error) {
                console.error('Error fetching firmware version init data:', error);
            }
        };
        fetchInitData();
    }, []);

    useEffect(() => {
        dispatch(fetchFirmwareVersions({ page, pageSize, sortColumn, sortDirection, filterVersion, filterDeviceType }));
    }, [dispatch, page, pageSize, sortColumn, sortDirection, filterVersion, filterDeviceType]);

    // Show error notification
    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification]);

    // Show success notification
    useEffect(() => {
        if (successMessage) {
            showNotification(successMessage, 'success');
            dispatch(clearSuccessMessage());
            // Refetch firmware versions to update the list
            dispatch(fetchFirmwareVersions({ page, pageSize, sortColumn, sortDirection, filterVersion, filterDeviceType }));
        }
    }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterVersion, filterDeviceType]);

    // Handle download firmware
    const handleDownload = async (row) => {
        if (!row.linkFile) {
            showNotification(t('firmwareVersions.downloadError'), 'error');
            return;
        }

        try {
            // Call download API with axios (includes authentication)
            const response = await api.get(`/firmwareversions/${row.id}/download`, {
                responseType: 'blob', // Important: receive file as blob
            });

            // Create blob URL and trigger download
            // Note: api.get returns response.data directly due to interceptor
            const blob = response instanceof Blob ? response : new Blob([response], { type: 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = row.fileName || `firmware_${row.version}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url); // Clean up

            showNotification(t('firmwareVersions.downloadSuccess'), 'success');
        } catch (error) {
            showNotification(error.message || t('firmwareVersions.downloadError'), 'error');
        }
    };

    // Define table columns
    const columns = [
        {
            key: 'version',
            label: t('firmwareVersions.table.version'),
        },
        {
            key: 'fileName',
            label: t('firmwareVersions.table.fileName'),
            render: (value) => (
                <Tooltip title={value || '-'} placement="top" arrow>
                    <Box
                        sx={{
                            maxWidth: '300px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                        }}
                    >
                        {value || '-'}
                    </Box>
                </Tooltip>
            ),
        },
        {
            key: 'deviceType',
            label: t('firmwareVersions.table.deviceType'),
            render: (value) => {
                const deviceTypeObj = deviceTypes.find((type) => type.id === value);
                return deviceTypeObj ? deviceTypeObj.name : '-';
            },
        },
        {
            key: 'createdOn',
            label: t('firmwareVersions.table.createdOn'),
            render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
        },
        {
            key: 'updatedOn',
            label: t('firmwareVersions.table.updatedOn'),
            render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
        },
    ];

    // Custom render for actions column (includes download button)
    const renderActions = (row) => (
        <>
            {/* Download button */}
            <Tooltip title={t('firmwareVersions.downloadFirmware')}>
                <span>
                    <IconButton
                        onClick={() => handleDownload(row)}
                        disabled={!row.linkFile}
                        color="primary"
                        size="small"
                        id={`download-${row.id}`}
                        name={`download-${row.id}`}
                        data-testid={`firmwareversions-button-download-${row.id}`}
                    >
                        <DownloadIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            {/* Edit button (if has permission) */}
            {canEditFirmwareVersion && (
                <IconButton
                    onClick={() => handleEdit(row)}
                    color="primary"
                    size="small"
                    id={`edit-${row.id}`}
                    name={`edit-${row.id}`}
                    data-testid={`firmwareversions-button-edit-${row.id}`}
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            )}

            {/* Delete button (if has permission) */}
            {canDeleteFirmwareVersion && (
                <IconButton
                    onClick={() => handleDelete(row)}
                    color="error"
                    size="small"
                    id={`delete-${row.id}`}
                    name={`delete-${row.id}`}
                    data-testid={`firmwareversions-button-delete-${row.id}`}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )}
        </>
    );

    // Handle edit action
    const handleEdit = (row) => {
        // Fetch firmware version details by ID
        dispatch(fetchFirmwareVersionById(row.id));
        setShowEditDialog(true);
    };

    // Handle delete action - show confirmation dialog
    const handleDelete = (row) => {
        setFirmwareVersionToDelete(row);
        setShowDeleteDialog(true);
    };

    // Confirm delete action
    const handleConfirmDelete = () => {
        if (firmwareVersionToDelete?.id) {
            dispatch(deleteFirmwareVersion(firmwareVersionToDelete.id));
        }
        setShowDeleteDialog(false);
        setFirmwareVersionToDelete(null);
    };

    // Cancel delete action
    const handleCancelDelete = () => {
        setShowDeleteDialog(false);
        setFirmwareVersionToDelete(null);
    };

    // Handle bulk delete - show confirmation dialog
    const handleBulkDelete = () => {
        setShowBulkDeleteDialog(true);
    };

    // Confirm bulk delete action
    const handleConfirmBulkDelete = () => {
        dispatch(bulkDeleteFirmwareVersions(selectedIds));
        setShowBulkDeleteDialog(false);
        setSelectedIds([]);
    };

    // Cancel bulk delete action
    const handleCancelBulkDelete = () => {
        setShowBulkDeleteDialog(false);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        dispatch(setPageSize(newPageSize));
    };

    // Handle sort change
    const handleSortChange = (column, direction) => {
        dispatch(setSortColumn(column));
        dispatch(setSortDirection(direction));
    };

    // Handle filter version change
    const handleFilterVersionChange = (event) => {
        dispatch(setFilterVersion(event.target.value));
    };

    // Handle filter device type change (pass raw value)
    const handleFilterDeviceTypeChange = (event) => {
        dispatch(setFilterDeviceType(event.target.value));
    };

    // Handle add firmware version - show dialog
    const handleAddFirmwareVersion = () => {
        dispatch(clearSelectedFirmwareVersion());
        setShowAddDialog(true);
    };

    // Handle close add firmware version dialog
    const handleCloseAddFirmwareVersion = () => {
        setShowAddDialog(false);
        dispatch(clearSelectedFirmwareVersion());
    };

    // Confirm add firmware version
    const handleConfirmAddFirmwareVersion = (firmwareVersionData) => {
        dispatch(addFirmwareVersion(firmwareVersionData));
        setShowAddDialog(false);
    };

    // Handle close edit firmware version dialog
    const handleCloseEditFirmwareVersion = () => {
        setShowEditDialog(false);
        dispatch(clearSelectedFirmwareVersion());
    };

    // Confirm edit firmware version
    const handleConfirmEditFirmwareVersion = (firmwareVersionData) => {
        dispatch(updateFirmwareVersion(firmwareVersionData));
        setShowEditDialog(false);
    };

    // Device type filter options (fetched from API)
    // Use useMemo to prevent re-creating array on every render
    // Note: "Select All" option should be first in the list
    const deviceTypeOptions = useMemo(() => {
        const allOption = { id: '', name: t('firmwareVersions.filter.allDeviceTypes') };

        // API returns array: [{ id: 1, name: "Type 1" }, { id: 2, name: "Type 2" }, ...]
        // Convert id to string for consistency with filterDeviceType (which is string)
        const deviceTypeArray = (deviceTypes || []).map((dt) => ({
            id: String(dt.id),
            name: dt.name,
        }));

        return [allOption, ...deviceTypeArray];
    }, [deviceTypes, t]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DescriptionIcon fontSize="small" />
                        <Typography variant="body2">{t('firmwareVersions.breadcrumb.licenseManagement')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('firmwareVersions.breadcrumb.firmwareVersions')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title and Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {t('firmwareVersions.title')}
                    </Typography>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {canAddFirmwareVersion && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleAddFirmwareVersion}
                                id="addFirmwareVersion"
                                name="addFirmwareVersion"
                                data-testid="firmwareversions-button-addFirmwareVersion"
                            >
                                {t('firmwareVersions.addFirmwareVersion')}
                            </Button>
                        )}
                        {canDeleteFirmwareVersion && (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleBulkDelete}
                                disabled={selectedIds.length === 0}
                                id="deleteSelected"
                                name="deleteSelected"
                                data-testid="firmwareversions-button-deleteSelected"
                            >
                                {t('firmwareVersions.deleteSelected')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}
            >
                {/* Filter Section */}
                <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Input
                            label={t('firmwareVersions.filterByVersion')}
                            value={filterVersion}
                            onChange={handleFilterVersionChange}
                            placeholder={t('firmwareVersions.filterByVersionPlaceholder')}
                            id="filterVersion"
                            name="filterVersion"
                            data-testid="firmwareversions-input-filterVersion"
                            sx={{ width: 320 }}
                        />
                        <Select
                            label={t('firmwareVersions.filterByDeviceType')}
                            value={filterDeviceType}
                            onChange={handleFilterDeviceTypeChange}
                            options={deviceTypeOptions}
                            id="filterDeviceType"
                            name="filterDeviceType"
                            data-testid="firmwareversions-select-filterDeviceType"
                            sx={{ width: 320 }}
                        />
                    </Box>
                </Box>

                {/* Table */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
                    <Table
                        id="firmwareVersions-table"
                        columns={columns}
                        data={firmwareVersions}
                        selectable={true}
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        renderActions={renderActions}
                        onRowDoubleClick={canEditFirmwareVersion ? handleEdit : null}
                        loading={loading}
                        emptyMessage={t('firmwareVersions.noData')}
                        idField="id"
                        pagination={true}
                        page={page}
                        pageSize={pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        sortable={true}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        sortableColumns={['version', 'deviceType', 'createdOn', 'updatedOn']}
                    />
                </Box>
            </Paper>

            {/* Confirm Delete Dialog */}
            <Dialog open={showDeleteDialog} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('firmwareVersions.deleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('firmwareVersions.deleteDialog.message', {
                            version: firmwareVersionToDelete?.version || '',
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelDelete}
                        variant="outlined"
                        id="cancelDelete"
                        name="cancelDelete"
                        data-testid="firmwareversions-button-cancelDelete"
                    >
                        {t('firmwareVersions.deleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmDelete"
                        name="confirmDelete"
                        data-testid="firmwareversions-button-confirmDelete"
                    >
                        {t('firmwareVersions.deleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Bulk Delete Dialog */}
            <Dialog open={showBulkDeleteDialog} onClose={handleCancelBulkDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('firmwareVersions.bulkDeleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('firmwareVersions.bulkDeleteDialog.message', {
                            count: selectedIds.length,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelBulkDelete}
                        variant="outlined"
                        id="cancelBulkDelete"
                        name="cancelBulkDelete"
                        data-testid="firmwareversions-button-cancelBulkDelete"
                    >
                        {t('firmwareVersions.bulkDeleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmBulkDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmBulkDelete"
                        name="confirmBulkDelete"
                        data-testid="firmwareversions-button-confirmBulkDelete"
                    >
                        {t('firmwareVersions.bulkDeleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Firmware Version Modal */}
            <AddFirmwareVersionModal
                open={showAddDialog}
                onClose={handleCloseAddFirmwareVersion}
                onConfirm={handleConfirmAddFirmwareVersion}
                deviceTypes={deviceTypes}
            />

            {/* Edit Firmware Version Modal */}
            <AddFirmwareVersionModal
                open={showEditDialog}
                onClose={handleCloseEditFirmwareVersion}
                onConfirm={handleConfirmEditFirmwareVersion}
                firmwareVersionData={selectedFirmwareVersion}
                editMode={true}
                deviceTypes={deviceTypes}
            />
        </Box>
    );
}

export default FirmwareVersions;
